import { EXPERT_TYPE_LABELS } from "@/consts/experts";
import { useFilterBySearchText } from "@/hooks/use-filter-by-search-text";
import { Expert } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { DropdownMenuLabel } from "@radix-ui/react-dropdown-menu";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useExpertsData } from "../../data/use-experts-data";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { useKitUuid } from "../../hooks/use-kit-uuid";
import { GetGroupExpertsResponseDto } from "../../lib/gen-api";
import { cn } from "../../lib/utils";
import { applyFilters, groupByKey, useFilter } from "../Filter/useFilter";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Column, Row } from "../ui/flex";
import { SearchInput } from "../ui/input";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { ExpertAvatar } from "./ExpertAvatar";

type ExpertSelectProps = {
  setExpertUuid: (expertUuid: string) => void;
  expertUuid?: string | null;
  autoSelect?: boolean;
  experts?: GetGroupExpertsResponseDto["data"];
  filterByType?: Expert["expertType"];
  onInviteExpertClick?: () => void;
};

export const ExpertSelect = forwardRef<HTMLButtonElement, ExpertSelectProps>(
  (
    {
      setExpertUuid,
      expertUuid,
      autoSelect,
      experts: propExperts,
      filterByType,
      onInviteExpertClick,
    },
    ref,
  ) => {
    const kitUuid = useKitUuid();
    const groupUuid = useGroupUuid();
    const [open, setOpen] = useState(false);
    const { experts: autoExperts } = useExpertsData(groupUuid, kitUuid);
    const experts = useMemo(
      () =>
        (propExperts ?? autoExperts).filter((expert) =>
          filterByType ? expert.expertType === filterByType : true,
        ),
      [propExperts, autoExperts, filterByType],
    );
    const preventClose = useRef(false);

    const expert = experts.find((expert) => expert.uuid === expertUuid);

    const {
      searchText,
      setSearchText,
      filteredItems: filteredExpertsByText,
    } = useFilterBySearchText(experts, ["name", "description"]);

    const { selected: selectedType, setSelected: setSelectedType } = useFilter(
      filteredExpertsByText,
      "expertType",
    );

    const filteredExperts = applyFilters(filteredExpertsByText, {
      expertType: selectedType,
    });
    const groups = groupByKey(filteredExpertsByText, "expertType");

    useEffect(() => {
      if (autoSelect && filteredExperts.length > 0 && !expertUuid) {
        setExpertUuid(filteredExperts[0].uuid);
      }
    }, [autoSelect, filteredExperts, setExpertUuid, expertUuid]);

    const handleOnAddExpertClick = () => {
      setOpen(false);
      onInviteExpertClick?.();
    };

    // Prevents closing when clicking on tabs
    const handleOnOpenChange = (isOpen: boolean) => {
      if (preventClose.current) {
        preventClose.current = false;
        return;
      }
      setOpen(isOpen);
    };

    return (
      <DropdownMenu open={open} onOpenChange={handleOnOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            ref={ref}
            size="sm"
            variant="ghost"
            className={cn("flex flex-1 flex-row items-center overflow-hidden", {
              "pl-0.5": expert,
            })}
          >
            {expert && <ExpertAvatar expert={expert} invertColors size="sm" />}
            <div className="flex-1 truncate">
              {expert?.name ?? "Select Expert"}
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top">
          <DropdownMenuLabel className="text-muted-foreground p-2 text-sm">
            Select Expert
          </DropdownMenuLabel>
          <DropdownMenuGroup>
            <div className="flex flex-row items-center justify-between gap-4 p-2 pb-4">
              <Tabs
                value={selectedType ?? "all"}
                onValueChange={(value) => {
                  preventClose.current = true;
                  setSelectedType(
                    value === "all" ? null : (value as Expert["expertType"]),
                  );
                }}
                onMouseUp={() => {
                  preventClose.current = false;
                }}
              >
                <TabsList className="flex flex-row gap-1">
                  {filterByType ? (
                    <TabsTrigger value={filterByType} disabled>
                      {EXPERT_TYPE_LABELS[filterByType]}
                    </TabsTrigger>
                  ) : (
                    <>
                      <TabsTrigger value="all">All</TabsTrigger>
                      {Object.entries(groups).map(([type, experts]) => {
                        if (experts.length === 0) return null;
                        return (
                          <TabsTrigger value={type} key={type}>
                            {EXPERT_TYPE_LABELS[type as Expert["expertType"]]}
                          </TabsTrigger>
                        );
                      })}
                    </>
                  )}
                </TabsList>
              </Tabs>
              <SearchInput
                placeholder="Search Expert..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
              />
            </div>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            {filteredExperts.map((expert, i) => (
              <DropdownMenuItem
                className={cn("cursor-pointer", {
                  "bg-background-10": expertUuid === expert.uuid,
                })}
                key={i}
                onClick={() => setExpertUuid(expert.uuid)}
              >
                <Row className="gap-2" align="center">
                  <ExpertAvatar expert={expert} size="lg" />
                  <Column className="gap-0.5">
                    <div className="text-xs font-semibold">{expert.name}</div>
                    {expert.group && groupUuid !== expert.group.uuid && (
                      <div className="text-foreground-5 text-xs">
                        by {expert.group.name}
                      </div>
                    )}
                    <div className="text-foreground-8 text-xs">
                      {expert.description}
                    </div>
                  </Column>
                </Row>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          {onInviteExpertClick && (
            <DropdownMenuGroup className="flex flex-row justify-end p-2">
              <Button
                onClick={handleOnAddExpertClick}
                variant="outline"
                size="sm"
              >
                <FontAwesomeIcon icon={faPlus} /> Invite Experts
              </Button>
            </DropdownMenuGroup>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);

ExpertSelect.displayName = "ExpertSelect";
